import { useEffect, useState } from "react";
import { Widget } from "@typeform/embed-react";
import { createShopifyCustomer } from "../../utils/create-shopify-customer";
import { checkIsValidProvince } from "../../utils/check-is-valid-province";
import { getTypeFormResponse } from "../../utils/get-typeform-response";
import { getCustomerDetails } from "../../utils/get-customer-details";
import { getOptions } from "../../utils/get-options";
import { calculateLoan } from "../../utils/calculate-loan";
import { useAppContext } from "../../context";
import productData from '../../data/product-data.json';
import { updateCustomerWithOrder } from "../../utils/update-customer-with-order";
import { extractQuizMetafields } from "../../utils/extract-quiz-fields";

export const QuizFormScreen = () => {
  const { setCurrentStep, setShopifyCustomer, setOptions, setTypeformAnswers } =
    useAppContext();

  const [isLoading, setIsLoading] = useState(false);
  const [fallbackUrl, setFallbackUrl] = useState(null);
  const [postalCodeFromParams, setPostalCodeFromParams] = useState(null);
  const [utmSourceFromParams, setUtmSourceFromParams] = useState(null);
  const [utmMediumFromParams, setUtmMediumFromParams] = useState(null);
  const [campaignNameFromParams, setcampaignNameFromParams] = useState(null);
  const [adGroupNameParams, setadGroupNameParams] = useState(null);
  const [keywordParams, setkeywordParams] = useState(null);

  // handle user is a renter
  const handleRenter = async (answersMap) => {
    console.log("User is a renter");

    const postalCode = postalCodeFromParams || answersMap.postal_code_q;

    const customerDetails = getCustomerDetails(answersMap);

    if (
      !customerDetails.firstName ||
      !customerDetails.lastName ||
      !customerDetails.email ||
      !customerDetails.phone
    ) {
      console.log(customerDetails);
      console.warn("Customer details are not complete");
      setIsLoading(false);
      setCurrentStep("customerIsRenter");
      setIsLoading(false);
      return;
    }

    console.log("Customer details: ", customerDetails);
    console.log("creating shopify customer");

    const shopifyCustomer = await createShopifyCustomer({
      ...customerDetails,
      postalCode,
      utmSource: utmSourceFromParams,
      utmMedium: utmMediumFromParams,
      campaignName: campaignNameFromParams,
      adgroupName: adGroupNameParams,
      keyword: keywordParams,
    });

    console.log("Shopify customer created: ", shopifyCustomer);

    setCurrentStep("customerIsRenter");
    setTypeformAnswers(answersMap);
    setIsLoading(false);
  };

  // handle user has plus 5 rooms
  const handleOwnerBedRoomPlusFive = async (answersMap) => {
    console.log("User is has 5+ rooms");

    const postalCode = postalCodeFromParams || answersMap.postal_code_q;

    const customerDetails = getCustomerDetails(answersMap);

    if (
      !customerDetails.firstName ||
      !customerDetails.lastName ||
      !customerDetails.email ||
      !customerDetails.phone
    ) {
      console.log(customerDetails);
      console.warn("Customer details are not complete");
      setIsLoading(false);
      setCurrentStep("customerRoomPlusFive");
      setIsLoading(false);
      return;
    }

    console.log("Customer details: ", customerDetails);
    console.log("creating shopify customer");

    const shopifyCustomer = await createShopifyCustomer({
      ...customerDetails,
      postalCode,
      utmSource: utmSourceFromParams,
      utmMedium: utmMediumFromParams,
      campaignName: campaignNameFromParams,
      adgroupName: adGroupNameParams,
      keyword: keywordParams,
    });

    console.log("Shopify customer created: ", shopifyCustomer);

    setCurrentStep("customerRoomPlusFive");
    setTypeformAnswers(answersMap);
    setIsLoading(false);
  };

  // handle typeform submit
  const handleTypeFormSubmit = async (props) => {
    setIsLoading(true);

    const { answers, answersMap } = await getTypeFormResponse(props.responseId);

    // console log the result coming from response
    console.log("Typeform response answers: ", answers);

    const postalCode = postalCodeFromParams || answersMap.postal_code_q;

    const isValidProvince = await checkIsValidProvince(postalCode);

    // validate user postal code
    if (!isValidProvince) {
      console.warn("User is outside of service area - invalid province");
      setCurrentStep("outOfServiceArea");
      setTypeformAnswers(answersMap);
      setIsLoading(false);
      return;
    }

    // Redirect if user is outside of service area, only postal code & email in answersMap
    if (Object.keys(answersMap).length === 2 && answersMap.email !== "") {
      setCurrentStep("outOfServiceArea");
      setTypeformAnswers(answersMap);
      setIsLoading(false);
      return;
    }

    // Redirect if user is a renter
    if (answersMap.owner === "Rent") {
      handleRenter(answersMap);
      return;
    }

    // Redirect if room number is 5+
    if (answersMap.room_number === "5+") {
      handleOwnerBedRoomPlusFive(answersMap);
      return;
    }

    const customerDetails = getCustomerDetails(answersMap);

    if (
      !customerDetails.firstName ||
      !customerDetails.lastName ||
      !customerDetails.email ||
      !customerDetails.phone
    ) {
      console.warn("Customer details are not complete");
      setIsLoading(false);
      window.location.href = fallbackUrl;
      return;
    }

    let options;

    try {
      options = getOptions(answers);
    } catch (error) {
      setIsLoading(false);
      if (error.message === "Total Btu is too high") {
        window.location.href = fallbackUrl;
      }
      return;
    }

    console.log("Creating Shopify customer with email:", customerDetails.email);
    const shopifyCustomer = await createShopifyCustomer({
      ...customerDetails,
      postalCode,
      utmSource: utmSourceFromParams,
      utmMedium: utmMediumFromParams,
      campaignName: campaignNameFromParams,
      adgroupName: adGroupNameParams,
      keyword: keywordParams,
    });
    console.log("Shopify customer created/updated:", shopifyCustomer);

    const optionsWithLoans = await Promise.all(
      options.map(async (option) => {
        // Calculate after-tax total
        const provTaxRate = (option.province && productData.prov_tax_rate[option.province]) || 1.13;
        const subtotal = option.total;
        const tax = subtotal * (provTaxRate - 1);
        const totalWithTax = subtotal + tax;
        const afterIncentive = Math.max(0, totalWithTax - option.grantAllowance);
        const loans = await calculateLoan(afterIncentive);
        return { ...option, loans };
      })
    );

    // update customer record with order information
    const tf = answersMap || {};
    
    // Extract unique questions/answers such as quiz utility and electrical panel size from all possible field refs
    const quizFields = extractQuizMetafields(tf, answers);
    
    const metafields = {
      // Postal code
      quiz_postal_code_q: tf.postal_code_q || tf.postal_code || "",
      quiz_user_postal_code: postalCode,
      // Owner type
      quiz_owner: tf.owner || "",
      // Home details
      quiz_home_type: tf.home_type || "",
      quiz_heat_source: tf.heat_source || "",
      quiz_heating_type: tf.heating_type || "",
      quiz_electric_panel: tf.electric_panel || "",
      quiz_system_age: tf.system_age || "",
      quiz_air_conditioner:
        typeof tf.air_conditioner !== "undefined"
          ? String(tf.air_conditioner)
          : "",
      // Square footage
      quiz_sq_ft_central: tf["sq_ft-Central"] || tf.sq_ft || "",
      quiz_sq_ft_ductless: tf["sq_ft-Ductless"] || "",
      quiz_condenser_mounted: tf.condenser_position || "",
      quiz_room_number: tf.room_number || "",
    };

    // Add unique answer from custom mapping - ex. quiz utility and electrical panel size
    metafields["quiz_utility"] = quizFields.quiz_utility;
    metafields["quiz_electrical_panel_size"] = quizFields.quiz_electrical_panel_size;

    // Add room size metafields dynamically based on room count
    if (tf.room_number === "2") {
      metafields["quiz_rooms_2_size_room_1"] = tf["rooms_2_size-room_1"] || "";
      metafields["quiz_rooms_2_size_room_2"] = tf["rooms_2_size-room_2"] || "";
    } else if (tf.room_number === "3") {
      metafields["quiz_rooms_3_size_room_1"] = tf["rooms_3_size-room_1"] || "";
      metafields["quiz_rooms_3_size_room_2"] = tf["rooms_3_size-room_2"] || "";
      metafields["quiz_rooms_3_size_room_3"] = tf["rooms_3_size-room_3"] || "";
    } else if (tf.room_number === "4") {
      metafields["quiz_rooms_4_size_room_1"] = tf["rooms_4_size-room_1"] || "";
      metafields["quiz_rooms_4_size_room_2"] = tf["rooms_4_size-room_2"] || "";
      metafields["quiz_rooms_4_size_room_3"] = tf["rooms_4_size-room_3"] || "";
      metafields["quiz_rooms_4_size_room_4"] = tf["rooms_4_size-room_4"] || "";
    }

    await updateCustomerWithOrder(shopifyCustomer.id, {
      metafields,
    });

    setCurrentStep("quote");
    setShopifyCustomer(shopifyCustomer);
    setOptions(optionsWithLoans);
    setTypeformAnswers(answersMap);
    setIsLoading(false);
  };

  // useEffect(() => {
  //   const fallbackUrl = document
  //     .getElementById("root")
  //     .getAttribute("data-fallback-page");

  //   if (!fallbackUrl) {
  //     console.warn("Fallback url not found");
  //     return;
  //   }

  //   setFallbackUrl(fallbackUrl);
  // }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const postalCode = searchParams.get("postal_code");
    const utmSource = searchParams.get("utm_source");
    const utmMedium = searchParams.get("utm_medium");
    const campaignName = searchParams.get("campaign_name");
    const adGroupName = searchParams.get("adgroup_name");
    const keyword = searchParams.get("keyword");

    if (postalCode) {
      setPostalCodeFromParams(postalCode);
    }
    if (utmSource) {
      setUtmSourceFromParams(utmSource);
    }
    if (utmMedium) {
      setUtmMediumFromParams(utmMedium);
    }
    if (campaignName) {
      setcampaignNameFromParams(campaignName);
    }
    if (adGroupName) {
      setadGroupNameParams(adGroupName);
    }
    if (keyword) {
      setkeywordParams(keyword);
    }
  }, []);

  if (isLoading) {
    return (
      <div className="tw-w-full tw-flex tw-items-center tw-justify-center tw-mt-40">
        <span className="loader !tw-border-blue"></span>
      </div>
    );
  }

  const quizId = process.env.REACT_APP_TYPEFORM_QUIZ_ID;

  return (
    <Widget
      id={quizId}
      style={{ width: "100%", height: "calc(100vh - 100px)" }}
      onSubmit={handleTypeFormSubmit}
      hidden={
        postalCodeFromParams ? { postal_code: postalCodeFromParams } : undefined
      }
      inlineOnMobile={true}
    />
  );
};
